import React, { useEffect, useState, useCallback } from "react";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { getGeo, getOpenRides, getMyRides, placeBid, advanceState, formatApiError } from "../lib/api";
import { OpenRideCard, ActiveTripCard } from "./DriverRideCards";

export default function DriverApp() {
  const [geo, setGeo] = useState({ cities: [] });
  const [city, setCity] = useState("Lagos");
  const [online, setOnline] = useState(false);
  const [open, setOpen] = useState([]);
  const [mine, setMine] = useState([]);
  const [bidAmounts, setBidAmounts] = useState({});

  const refresh = useCallback(async () => {
    try {
      setMine(await getMyRides());
      if (online) setOpen(await getOpenRides(city)); else setOpen([]);
    } catch (e) { console.error("Failed to refresh driver data:", e); }
  }, [online, city]);

  useEffect(() => { getGeo().then(setGeo).catch((e) => console.error("Failed to load geo:", e)); }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 4000); return () => clearInterval(t); }, [refresh]);

  const bid = async (rideId) => {
    const amt = parseInt(bidAmounts[rideId], 10);
    if (!amt) return toast.error("Enter a bid amount.");
    try { await placeBid(rideId, amt); toast.success("Bid submitted!"); refresh(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const setAmount = (id, v) => setBidAmounts((prev) => ({ ...prev, [id]: v }));
  const step = async (id, state) => {
    try { await advanceState(id, state); refresh(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const activeMine = mine.filter((r) => !["TRIP_COMPLETED", "PASSENGER_CANCELLED", "DRIVER_CANCELLED", "NO_SHOW", "SYSTEM_CANCELLED", "BIDDING"].includes(r.state) && r.driver_id);

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
      <div className="space-y-6">
        <div className="rounded-3xl border border-white/10 bg-[#0e1311] p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display font-extrabold text-white text-2xl">Driver</h2>
              <p className="text-white/50 text-sm mt-1">{online ? "You're online and receiving requests" : "You're offline"}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-semibold ${online ? "text-[#2fd645]" : "text-white/50"}`}>{online ? "Online" : "Offline"}</span>
              <Switch data-testid="driver-online-switch" checked={online} onCheckedChange={setOnline} />
            </div>
          </div>
          <div className="mt-4">
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="driver-city-select" className="h-11 bg-[#0a0e0c] border-white/15 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0e1311] border-white/15 text-white">
                {geo.cities.map((c) => <SelectItem key={c.name} value={c.name} className="focus:bg-[#2fd645]/15 focus:text-white">{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <h3 className="font-display font-bold text-white text-lg mb-3">Ride requests {online && `(${open.length})`}</h3>
          {!online && <p className="text-white/50 text-sm">Go online to see nearby ride requests.</p>}
          {online && open.length === 0 && <p className="text-white/40 text-sm">No open requests right now…</p>}
          <div className="space-y-3">
            {open.map((r) => <OpenRideCard key={r.id} ride={r} amount={bidAmounts[r.id]} onAmount={setAmount} onBid={bid} />)}
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-white text-lg mb-3">Active trips</h3>
        {activeMine.length === 0 && <p className="text-white/50 text-sm">No active trips. Win a bid to start driving.</p>}
        <div className="space-y-3">
          {activeMine.map((r) => <ActiveTripCard key={r.id} ride={r} onStep={step} />)}
        </div>
      </div>
    </div>
  );
}
